import { createContext, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "../assets/images/logo-dark.png";
import { UserLogout } from '../api/autoLogOutService';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const SESSION_EXPIRATION_MS = 60 * 60 * 1000;

    const defaultUser = useMemo(() => ({
        LEDGER_ID: "00000000-0000-0000-0000-000000000000",
        TOKEN: null,
        COMPANYLOGO: Logo,
        COMPANYNAME: "Midamas",
        USERNAME: "",
        PROFILEIMAGE: ""
    }), []);

    const [user, setUser] = useState(defaultUser);
    const [loading, setLoading] = useState(true);
    const [toasts, setToasts] = useState([]);

    const showToast = (toastData) => {
        setToasts((prev) => [...prev, { id: Date.now(), ...toastData }]);
    };

    const removeToast = (id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    };

    useEffect(() => {
        try {
            const storedData = localStorage.getItem("authUser");
            if (storedData) {
                const parsedData = JSON.parse(storedData);
                parsedData.COMPANYLOGO = parsedData.COMPANYLOGO || Logo;
                parsedData.COMPANYNAME = parsedData.COMPANYNAME || "Midamas";
                parsedData.USERNAME = parsedData.USERNAME || "";
                parsedData.PROFILEIMAGE = parsedData.PROFILEIMAGE || "";
                setUser(parsedData);
                const loginTime = localStorage.getItem("LOGIN_TIME");
                if (loginTime && Date.now() - parseInt(loginTime, 10) > SESSION_EXPIRATION_MS) {
                    logout();
                }
            }
        } catch (error) {
            console.error("Failed to parse user data from localStorage:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    const login = useCallback((userData) => {
        try {
            userData.COMPANYLOGO = userData.COMPANYLOGO || Logo;
            userData.COMPANYNAME = userData.COMPANYNAME || "Midamas";
            userData.USERNAME = userData.USERNAME || "";
            userData.PROFILEIMAGE = userData.PROFILEIMAGE || "";

            localStorage.setItem("LOGIN_RESPONSE", JSON.stringify(userData));
            localStorage.setItem("LOGIN_TIME", Date.now().toString());
            if (userData.JWTTOKEN) localStorage.setItem("authToken", userData.JWTTOKEN);

            import('../Context/authLogout').then((module) => {
                module.clearForceLogout();
            });

            setUser(userData);
        } catch (error) {
            console.error("Failed to save user data to localStorage:", error);
        }
    }, [navigate]);

    const logout = useCallback(async () => {
        try {
            const response = await UserLogout();
            if (response?.Message?.[0]?.RETURNCODE === "1" || response?.Message?.[0]?.RETURNCODE === "0") {
                const rememberedUser = localStorage.getItem("rememberedUser");
                const rememberedPassword = localStorage.getItem("rememberedPassword");

                localStorage.clear();
                sessionStorage.clear();
                if (rememberedUser) localStorage.setItem("rememberedUser", rememberedUser);
                if (rememberedPassword) localStorage.setItem("rememberedPassword", rememberedPassword);

                setUser(defaultUser);
                navigate("/login", { replace: true });
            } else {
                const rememberedUser = localStorage.getItem("rememberedUser");
                const rememberedPassword = localStorage.getItem("rememberedPassword");

                localStorage.clear();
                sessionStorage.clear();
                if (rememberedUser) localStorage.setItem("rememberedUser", rememberedUser);
                if (rememberedPassword) localStorage.setItem("rememberedPassword", rememberedPassword);
                console.log(response?.Message?.[0]?.RETURNMESSAGE, "Logout Error");

                setUser(defaultUser);
                navigate("/login", { replace: true });
            }
        } catch (error) {
            console.error("Failed to logout:", error);

            const rememberedUser = localStorage.getItem("rememberedUser");
            const rememberedPassword = localStorage.getItem("rememberedPassword");

            localStorage.clear();
            sessionStorage.clear();
            if (rememberedUser) localStorage.setItem("rememberedUser", rememberedUser);
            if (rememberedPassword) localStorage.setItem("rememberedPassword", rememberedPassword);
            console.log(error, "Logout Error");

            setUser(defaultUser);
            navigate("/login", { replace: true });
        }
    }, [defaultUser, navigate]);

    useEffect(() => {
        const handleForceLogout = () => {
            const reason = localStorage.getItem("LOGOUT_REASON");
            if (reason === "SESSION_EXPIRED") {
                showToast({
                    title: "Session Expired",
                    message: "Your session has expired. Please login again.",
                    type: "warning",
                });
                logout();
            }
            else if (reason === "SILENT_SESSION_RESET") {
                logout();  
            }
        };

        window.addEventListener("FORCE_LOGOUT_EVENT", handleForceLogout);
        return () => {
            window.removeEventListener("FORCE_LOGOUT_EVENT", handleForceLogout);
        };
    }, [logout]);

    const checkIsUserLoggedIn = useCallback(() => {
        return user.LEDGER_ID !== defaultUser.LEDGER_ID;
    }, [user, defaultUser]);

    const updateCompanyLogo = useCallback((newLogoURL) => {
        setUser((prevUser) => {
            const updatedUser = { ...prevUser, COMPANYLOGO: newLogoURL };
            localStorage.setItem("LOGIN_RESPONSE", JSON.stringify(updatedUser));
            return updatedUser;
        });
    }, []);

    const updateProfileImage = useCallback((newImageUrl) => {
        setUser((prevUser) => {
            const updatedUser = { ...prevUser, PROFILEIMAGE: newImageUrl };
            localStorage.setItem("authUser", JSON.stringify(updatedUser));
            return updatedUser;
        });
    }, []);

    useEffect(() => {
        if (checkIsUserLoggedIn()) {
            const loginTime = parseInt(localStorage.getItem("LOGIN_TIME") || "0", 10);
            const remainingTime = SESSION_EXPIRATION_MS - (Date.now() - loginTime);

            if (remainingTime <= 0) {
                logout();
            } else {
                const timer = setTimeout(() => {
                    logout();
                }, remainingTime);

                return () => clearTimeout(timer);
            }
        }
    }, [user, logout, checkIsUserLoggedIn]);

    const contextValue = useMemo(() => ({
        user,
        login,
        logout,
        checkIsUserLoggedIn,
        updateCompanyLogo,
        updateProfileImage,
        loading,
        toasts,
        removeToast,
    }), [user, login, logout, checkIsUserLoggedIn, updateCompanyLogo, loading, toasts, updateProfileImage]);

    return (
        <AuthContext.Provider value={contextValue}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
